/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output, forwardRef,
} from '@angular/core';
import { FeedItemExtraAction } from '../../common/community/feed/feed-item/feed-item-extra-action';
import { ActionState } from '../../common/community/feed/actions/pending';
import { itemContext } from '../../common/community/feed/community-item';

// Material Symbols "keep" / "keep_off": not in the Material Icons v4 font this app ships.
export const KEEP_PATH = 'M640-480l80 80v80H520v240l-40 40-40-40v-240H240v-80l80-80v-280h-40v-80h400v80h-40v280Zm-286 80h252l-46-46v-314H400v314l-46 46Zm126 0Z';
export const KEEP_OFF_PATH = 'M680-840v80h-40v327l-80-80v-247H400v87l-87-87-33-33v-47h400ZM480-40l-40-40v-240H240v-80l80-80v-46L56-792l56-56 736 736-58 56-264-264h-6v240l-40 40ZM354-400h92l-44-44-2-2-46 46Zm126-193Zm-78 149Z';

// Pin / unpin one of your own articles or statuses to the top of your profile, styled like Subscribe:
// "Pinned" previews "Unpin" on hover once the pointer has left after pinning. Projected into the feed
// card with `feedItemAction`; as a FeedItemExtraAction the card also offers it in its ⋯ menu on phones.
@Component({
  selector: 'ut-community-profile-pin-action',
  templateUrl: './community-profile-pin-action.component.html',
  styleUrls: ['../../common/community/feed/actions/action-button.scss', './community-profile-pin-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{ provide: FeedItemExtraAction, useExisting: forwardRef(() => CommunityProfilePinActionComponent) }],
})
export class CommunityProfilePinActionComponent implements FeedItemExtraAction {
  @Input({ required: true }) item: any;
  // Emits after the server saves, so the list can re-sort with the pinned item first.
  @Output() changed = new EventEmitter<boolean>();

  readonly state = new ActionState();

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('AlertService') private AlertService: any,
  ) {}

  // The author's pinned_content holds one pinned id per type.
  get pinned(): boolean {
    return this.item.user?.pinned_content?.[`${this.item.type}_id`] === this.item.id;
  }

  readonly keepPath = KEEP_PATH;
  readonly keepOffPath = KEEP_OFF_PATH;

  get menuLabel(): string {
    return this.pinned ? 'Unpin from profile' : 'Pin to profile';
  }

  get menuIconPath(): string {
    return this.pinned ? KEEP_OFF_PATH : KEEP_PATH;
  }

  trigger(): void {
    this.toggle();
  }

  get label(): string {
    return `${this.pinned ? 'Pinned' : 'Pin to profile'}: ${itemContext(this.item)}`;
  }

  toggle(): void {
    if (this.state.busy()) return;
    const pin = !this.pinned;
    const key = `${this.item.type}_id`;
    this.state.run(this.UserService.updateMe({ pinned_content: { [key]: pin ? this.item.id : null } })).then(() => {
      this.item.user.pinned_content = { ...this.item.user.pinned_content, [key]: pin ? this.item.id : null };
      this.state.complete(pin ? 'on' : 'off', pin ? 'Pinned to the top of your profile' : 'Unpinned from your profile');
      this.changed.emit(pin);
    }, () => this.AlertService.addDanger('There was an error'));
  }
}
